import { useState } from "react";
import { cn } from "@/lib/utils";

/**
 * ItemEditorBar — the edit affordance row shared by board detail overlays.
 * Rename (title), edit the why-text, move up/down, archive, and (for Jeff's
 * own not-yet-started ideas) remove entirely. Every action rides the same
 * action endpoint and leaves a plain-English confirmation behind.
 *
 * Props:
 *   board    the parent's useBoard() hook object (not a board name)
 *   item     { slug, kind, state, payload } — the row being edited
 *   onClose  called after archive/delete so the open overlay follows the row
 */
export const ItemEditorBar = ({ board: b, item, onClose, testId = "item-editor" }) => {
  const [mode, setMode] = useState(null); // "rename" | "text" | null
  const [value, setValue] = useState("");
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  if (!item) return null;
  const { slug, kind, state, payload = {} } = item;
  const isIdea = kind === "idea";
  const canDelete = isIdea && state === "INTAKE";
  const archived = state === "ARCHIVED";

  const finish = (res, word) => {
    if (res) {
      setMode(null);
      setValue("");
      setDone(word);
      setTimeout(() => setDone(null), 2500);
    }
  };
  const fail = (e) => setError(e?.message || "That didn't go through — try again.");

  const runEdit = async (verb, fields, word) => {
    setError(null);
    try {
      const res = await b.edit(verb, slug, fields);
      finish(res, word);
      return res;
    } catch (e) {
      fail(e);
      return null;
    }
  };
  const runAct = async (verb, note, word) => {
    setError(null);
    try {
      const res = await b.act(verb, slug, note);
      finish(res, word);
      return res;
    } catch (e) {
      fail(e);
      return null;
    }
  };

  const btn =
    "rounded-control border border-os-border px-2.5 py-1.5 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-50";

  return (
    <div
      data-testid={testId}
      data-slug={slug}
      className="mt-6 border-t border-os-border pt-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
          Make a change
        </span>
        {!archived && (
          <>
            <button type="button" data-testid={`${testId}-rename`} className={btn} onClick={() => { setMode("rename"); setValue(payload.title || ""); }}>
              Rename
            </button>
            <button type="button" data-testid={`${testId}-edit-text`} className={btn} onClick={() => { setMode("text"); setValue(payload.why || payload.description || ""); }}>
              Edit the words
            </button>
            <button type="button" data-testid={`${testId}-up`} className={btn} disabled={Boolean(b.pending)} onClick={() => runEdit("reorder", { move: -1 }, "Moved up")}>
              Move up
            </button>
            <button type="button" data-testid={`${testId}-down`} className={btn} disabled={Boolean(b.pending)} onClick={() => runEdit("reorder", { move: 1 }, "Moved down")}>
              Move down
            </button>
          </>
        )}
        <button
          type="button"
          data-testid={`${testId}-archive`}
          className={btn}
          disabled={archived || Boolean(b.pending)}
          onClick={async () => {
            const res = await runAct("archiveAny", undefined, "Set aside");
            if (res && onClose) onClose();
          }}
        >
          {archived ? "Set aside" : "Set aside"}
        </button>
        {canDelete && (
          <button
            type="button"
            data-testid={`${testId}-delete`}
            className={cn(btn, "text-os-red")}
            onClick={async () => {
              const res = await b.edit("deleteIdea", slug);
              if (res) {
                setDone("Removed");
                if (onClose) onClose();
              }
            }}
          >
            Remove for good
          </button>
        )}
      </div>
      {mode && (
        <form
          data-testid={`${testId}-form`}
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (mode === "rename") await runEdit("rename", { value: value.trim() }, "Renamed");
            else await runEdit("editText", { value: value.trim(), note: payload.why !== undefined ? "why" : "description" }, "Words updated");
          }}
        >
          <input
            data-testid={`${testId}-input`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            placeholder={mode === "rename" ? "The new name" : "The new words"}
            className="min-w-[14rem] flex-1 rounded-control border border-os-border bg-white px-2.5 py-1.5 text-[13px] outline-none focus:border-os-border-strong"
          />
          <button type="submit" data-testid={`${testId}-save`} disabled={!value.trim() || Boolean(b.pending)} className={btn}>
            {b.pending === slug ? "Saving…" : "Save"}
          </button>
          <button type="button" className="rounded-control px-2 py-1.5 font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink" onClick={() => setMode(null)}>
            Cancel
          </button>
        </form>
      )}
      {done && (
        <p data-testid={`${testId}-done`} className="mt-2 text-[0.8125rem] font-medium text-os-green">
          {done} — the board shows it straight away.
        </p>
      )}
      {error && (
        <p data-testid={`${testId}-error`} className="mt-2 text-[0.8125rem] text-os-red">
          {error}
        </p>
      )}
      {b.notice?.text && (
        <p className="mt-2 text-[0.8125rem] text-os-amber">{b.notice.text}</p>
      )}
    </div>
  );
};

export default ItemEditorBar;